/**
 * Status of one transaction, from POST /api/tx_lookup (app/modules/TxLookup.rb). The
 * server fetches and caches it; the browser never calls a block explorer directly.
 */
export interface TxInput {
  txid: string;
  address: string | null;
  sats: number;
  unconfirmed_parent?: boolean;
}

export interface TxOutput {
  address: string | null;
  sats: number;
  type: string;
}

export interface FeeTiers {
  fastest: number;
  half_hour: number;
  hour: number;
  economy: number;
  minimum: number;
  source: string;
}

export interface TxLookupResult {
  txid: string;
  source: string;
  fetched_at: string;
  confirmed: boolean;
  block_height: number | null;
  block_time: number | null;
  coinbase: boolean;
  fee_sats: number;
  vsize: number;
  fee_rate: number | null;
  rbf_signaled: boolean;
  input_count: number;
  input_sats: number;
  inputs: TxInput[];
  output_count: number;
  output_sats: number;
  outputs: TxOutput[];
  tip_height: number | null;
  btc_usd_price: number | null;
  // confirmed only
  confirmations?: number;
  // unconfirmed only
  first_seen?: number;
  package?: {
    effective_fee_rate: number;
    ancestor_count: number;
    ancestor_fee_sats: number;
    ancestor_vsize: number;
    descendant_count: number;
  };
  effective_fee_rate?: number | null;
  fees?: FeeTiers | null;
  queue?: { blocks_away: number; or_more: boolean } | null;
  advice?: {
    target_fee_rate: number;
    rbf_fee_sats: number;
    rbf_fee_rate: number;
    rbf_extra_sats: number;
    cpfp_child_fee_sats: number;
    cpfp_child_fee_rate: number;
    child_vsize: number;
  } | null;
}

/** result on success; otherwise error (notFound / login flags for the page) */
export interface TxLookupResponse {
  result: TxLookupResult | null;
  error: string | null;
  notFound: boolean;
  login: boolean;
}

const UNAVAILABLE =
  "We couldn't check this transaction right now. Please try again in a minute.";

export async function lookupTx(txid: string): Promise<TxLookupResponse> {
  try {
    const response = await fetch("/api/tx_lookup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") ?? "",
      },
      body: JSON.stringify({ txid }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      return { result: data as TxLookupResult, error: null, notFound: false, login: false };
    }
    return {
      result: null,
      error: data.error || UNAVAILABLE,
      notFound: data.status === "not_found",
      login: data.login === true,
    };
  } catch {
    return { result: null, error: UNAVAILABLE, notFound: false, login: false };
  }
}

/** Latest block from our own copy (GET /api/chain_tip); null when it can't be read. */
export async function fetchChainTip(): Promise<{ height: number; block_time: number | null } | null> {
  try {
    const response = await fetch("/api/chain_tip");
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export type ParsedInput =
  | { kind: "txid"; txid: string }
  | { kind: "address" | "invoice" | "lightning_address" | "invalid"; txid: null };

/**
 * A txid on its own, or inside a block explorer link (mempool.space/tx/…,
 * blockstream.info/tx/…, blockchair, blockchain.com). Things people paste by mistake
 * (an address, an invoice, a Lightning Address) are recognised so the page can point to
 * the right tool.
 */
export function parseTxInput(raw: string): ParsedInput {
  const value = raw.trim();
  const hex = value.match(/(?:^|[^0-9a-fA-F])([0-9a-fA-F]{64})(?:$|[^0-9a-fA-F])/);
  if (hex) return { kind: "txid", txid: hex[1].toLowerCase() };

  const lower = value.toLowerCase();
  if (/^(lightning:)?ln(bc|tb|bcrt)[0-9a-z]+$/.test(lower)) return { kind: "invoice", txid: null };
  if (/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(value)) return { kind: "lightning_address", txid: null };
  if (/^(bitcoin:)?(bc1|tb1|[13mn2])[0-9a-z]{20,90}$/i.test(value)) return { kind: "address", txid: null };
  return { kind: "invalid", txid: null };
}

export const formatRate = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined) return "–";
  const digits = rate < 10 ? 2 : 1;
  return `${Number(rate.toFixed(digits)).toLocaleString("en-US")} sat/vB`;
};

/** "3 h 12 min", "4 min", "2 days 3 h" */
export const formatDuration = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return "less than a minute";
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ${minutes % 60} min`;
  return `${Math.floor(hours / 24)} days ${hours % 24} h`;
};

export const formatUtc = (unixSeconds: number): string =>
  new Date(unixSeconds * 1000).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }) + " UTC";
